import { memo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { FormHelperText } from '@material-ui/core';
import ArrowTooltip from '../../../arrowTooltip';
import ActionButton from '../../../actionButton';
import InputField from '../../../inputField';
import CustomRichTextEditor from '../../../customRichTextEditor';
import RichTextPreview from '../../../richTextPreview';
import DotsMenu from '../../../dotsMenu';
import { QUESTIONNAIRE_QUESTION_MIN_OPTIONS } from '../../../../../constants/survey';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import {
  ANSWER_TITLE_ERROR,
  QUESTION_TITLE_ERROR,
  TEXT,
  DELAY,
  getQuestionActionItems,
} from './config';
import {
  replaceObjectInList,
  asyncDebounce,
  isObjectEmpty,
} from '../../../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  questionWrapper: {
    marginBottom: spacing(6),
  },
  headerSection: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
  },
  answersWrapper: {
    padding: spacing(6, 0, 0, 9),
  },
  verticalAnswerRichTextWrapper: {
    marginBottom: spacing(2),
  },
  inputTitleStyle: {
    marginBottom: 0,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 18,
    lineHeight: '22px',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: spacing(1),
  },
  tooltip: {
    fontFamily: 'ProximaNova-Regular',
    fontSize: '14px',
    lineHeight: '20px',
    color: primary.bluish2,
    margin: '7px 8px',
  },
  titleErrorMessage: {
    position: 'absolute',
    bottom: -20,
  },
  dragButton: {
    marginRight: spacing(1.5),
  },
  mainRichText: {
    width: '100%',
  },
  richTextEditorWrapper: {
    width: '100%',
  },
  textEditor: {
    height: 170,
    marginBottom: spacing(0.5),
    '& .ql-container': {
      height: 150,
    },
  },
  richTextBorder: {
    border: `1px solid ${primary.bluish6}`,
    padding: spacing(4),
    boxSizing: 'border-box',
    borderRadius: spacing(2),
    width: '100%',
  },
  richTextBorderWithError: {
    marginBottom: spacing(3),
  },
  richTextEditorWithError: {
    top: 198,
    paddingLeft: spacing(0.5),
  },
  errorMessage: {
    position: 'relative',
    left: -15,
    bottom: -37,
  },
  collapseButton: {
    alignSelf: 'flex-start',
    marginLeft: spacing(2),
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
    width: 12,
    height: 12,
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  dotsMenu: {
    transform: 'rotate(90deg)',
  },
  verticalAnswerWrapperWithDelete: {
    display: 'grid',
    gridTemplateColumns: '1fr 30px',
    gridColumnGap: spacing(1.5),
    alignItems: 'center',
    marginBottom: spacing(2),
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
  verticalAnswerWrapperWithDeleteFocused: {
    marginBottom: spacing(10),
  },
  verticalAnswerContent: {
    minWidth: 0,
  },
  deleteAnswerSpacer: {
    width: 30,
  },
}));

const QuestionnaireQuestionEntry = ({
  id = undefined,
  item,
  errors = {},
  onDuplicate,
  onDelete,
  onSave,
  onPendingChange = undefined,
  onRegisterFlush = undefined,
  translations,
  isOpenEndedAttribute,
  hasDefinedAnswers = false,
  dragRef = () => {},
  dragProps = null,
  minAnswers = QUESTIONNAIRE_QUESTION_MIN_OPTIONS,
}) => {
  const classes = useStyles();
  const [options, setOptions] = useState(item.options ?? []);
  const [titleText, setTitleText] = useState(item.text);
  const [isRichTextEditor, setIsRichTextEditor] = useState(
    Array(item.options?.length ?? 0).fill(false)
  );
  const [isExpanded, setIsExpanded] = useState(true);

  const savePayloadRef = useRef({
    item,
    onSave,
    isOpenEndedAttribute,
    options,
    titleText,
  });

  savePayloadRef.current = {
    item,
    onSave,
    isOpenEndedAttribute,
    options,
    titleText,
  };

  const handleInputChangeRef = useRef(null);
  if (!handleInputChangeRef.current) {
    handleInputChangeRef.current = asyncDebounce(async () => {
      const {
        onSave: save,
        item: currentItem,
        isOpenEndedAttribute: isOpenEnded,
        options: currentOptions,
        titleText: currentTitleText,
      } = savePayloadRef.current;

      const preparedData = {
        ...currentItem,
        options: isOpenEnded ? [] : currentOptions,
        text: currentTitleText,
        optionsCount: isOpenEnded ? 0 : currentOptions.length,
      };
      await save(preparedData);
    }, DELAY);
  }

  const handleInputChange = handleInputChangeRef.current;

  const getPreparedData = (currentOptions, currentTitleText) => ({
    ...item,
    options: isOpenEndedAttribute ? [] : currentOptions,
    text: currentTitleText,
    optionsCount: isOpenEndedAttribute ? 0 : currentOptions.length,
  });

  useEffect(() => {
    if (!onRegisterFlush) {
      return undefined;
    }

    return onRegisterFlush(() => handleInputChangeRef.current.flush());
  }, [onRegisterFlush]);

  const prevOptionsLengthRef = useRef(item.options?.length ?? 0);

  useEffect(() => {
    const optionsLength = item.options?.length ?? 0;

    if (prevOptionsLengthRef.current !== optionsLength) {
      prevOptionsLengthRef.current = optionsLength;
      setOptions(item.options ?? []);
      setTitleText(item.text);
      setIsRichTextEditor(Array(optionsLength).fill(false));
    }
  }, [item.options, item.options?.length, item.text]);

  const tooltipText = () => (
    <div className={classes.tooltip}>{translations.draggableTooltip}</div>
  );

  const handleTitleChange = e => {
    const { value } = e.target;
    savePayloadRef.current = {
      ...savePayloadRef.current,
      titleText: value,
    };
    setTitleText(value);
    onPendingChange?.(getPreparedData(options, value));
    handleInputChange();
  };

  const handleAnswerChange = (value, index) => {
    const newOptions = replaceObjectInList(options, index, {
      [TEXT]: value,
    });

    savePayloadRef.current = {
      ...savePayloadRef.current,
      options: newOptions,
    };
    setOptions(newOptions);
    onPendingChange?.(getPreparedData(newOptions, titleText));
    handleInputChange();
  };

  const handleExpandQuestion = () => {
    setIsExpanded(prev => !prev);
    if (isExpanded) {
      setIsRichTextEditor(Array(item.options?.length ?? 0).fill(false));
    }
  };

  const handleDeleteAnswer = index => {
    if (options.length <= minAnswers) {
      return;
    }

    const newOptions = options.filter((_, i) => i !== index);

    savePayloadRef.current = {
      ...savePayloadRef.current,
      options: newOptions,
    };
    setOptions(newOptions);
    setIsRichTextEditor(Array(newOptions.length).fill(false));
    onPendingChange?.(getPreparedData(newOptions, titleText));
    handleInputChange();
  };

  const handleOpenRichTextEditor = index => {
    setIsRichTextEditor(isRichTextEditor.map((val, i) => index === i));
  };

  const titleHasError = !!errors?.[QUESTION_TITLE_ERROR];
  const showDeleteAnswerButton = options.length > minAnswers;

  return (
    <div
      ref={dragRef}
      id={id}
      {...(dragProps ? { ...dragProps.draggableProps } : {})}
      className={classes.questionWrapper}
    >
      <Grid container spacing={0} className={classes.headerSection}>
        <Grid item xs={11} className={classes.leftSection}>
          <ArrowTooltip tooltipLabel={tooltipText()} position="top">
            <ActionButton
              className={classes.dragButton}
              type={ACTION_BUTTON_TYPES.DRAG}
              {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
            />
          </ArrowTooltip>
          <InputField
            className={classes.inputTitleStyle}
            customErrorClass={classes.titleErrorMessage}
            value={titleText}
            placeholder={translations.placeholders.questionPlaceholder}
            error={titleHasError}
            errorMessage={translations[errors?.[QUESTION_TITLE_ERROR]]}
            onClick={e => e.stopPropagation()}
            onChange={handleTitleChange}
            fullWidth
            multiline
          />
        </Grid>
        <Grid item xs={1} className={classes.actionButtons}>
          {!isOpenEndedAttribute && (
            <ActionButton
              className={classes.collapseButton}
              iconClass={classNames(classes.expandIcon, {
                [classes.expandIconOpened]: isExpanded,
              })}
              size="small"
              edge="end"
              type={ACTION_BUTTON_TYPES.ARROW}
              onClickHandler={handleExpandQuestion}
            />
          )}
          <DotsMenu
            iconButtonClass={classes.dotsMenu}
            menuItems={getQuestionActionItems(
              translations.questionActions,
              item,
              onDuplicate,
              onDelete,
              !isObjectEmpty(errors)
            )}
          />
        </Grid>
      </Grid>
      {isExpanded && hasDefinedAnswers && (
        <Grid item xs={12} className={classes.answersWrapper}>
          {options.map((option, index) => {
            const fieldName = `${ANSWER_TITLE_ERROR}_${index}`;
            const answerHasError = !!errors?.[fieldName];
            const inputValue = option.text;
            const isFocused = isRichTextEditor[index];
            const errorMessage = translations[errors?.[fieldName]];
            const modules = {
              toolbar: [['bold', 'italic', { list: 'bullet' }]],
            };

            const answerFields = (
              <>
                {isFocused && (
                  <div className={classes.richTextEditorWrapper} tabIndex={0}>
                    <CustomRichTextEditor
                      customEditorClass={classes.textEditor}
                      placeholder={translations.placeholders.answer}
                      customErrorClass={classNames({
                        [classes.richTextEditorWithError]: answerHasError,
                      })}
                      modules={modules}
                      className={classes.mainRichText}
                      value={inputValue || ''}
                      hasError={answerHasError}
                      errorMessage={errorMessage}
                      onUpdateText={value => handleAnswerChange(value, index)}
                    />
                  </div>
                )}
                {!isFocused && (
                  <div
                    className={classNames(
                      {
                        [classes.richTextBorderWithError]: answerHasError,
                      },
                      classes.richTextBorder
                    )}
                    onClick={() => handleOpenRichTextEditor(index)}
                  >
                    <RichTextPreview text={inputValue} />
                    {answerHasError && errorMessage && (
                      <FormHelperText
                        className={classes.errorMessage}
                        error={answerHasError}
                      >
                        {errorMessage}
                      </FormHelperText>
                    )}
                  </div>
                )}
              </>
            );

            return (
              <div
                key={`option_${option.id}`}
                className={classNames({
                  [classes.verticalAnswerWrapperWithDelete]: true,
                  [classes.verticalAnswerWrapperWithDeleteFocused]: isFocused,
                  [classes.verticalAnswerRichTextWrapper]:
                    !isFocused && !answerHasError,
                })}
              >
                <div className={classes.verticalAnswerContent}>
                  {answerFields}
                </div>
                {showDeleteAnswerButton ? (
                  <ActionButton
                    type={ACTION_BUTTON_TYPES.DELETE_FILL}
                    onClickHandler={() => handleDeleteAnswer(index)}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className={classes.deleteAnswerSpacer}
                  />
                )}
              </div>
            );
          })}
        </Grid>
      )}
    </div>
  );
};

QuestionnaireQuestionEntry.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  item: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  hasDefinedAnswers: PropTypes.bool,
  dragRef: PropTypes.func,
  dragProps: PropTypes.object,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPendingChange: PropTypes.func,
  onRegisterFlush: PropTypes.func,
  errors: PropTypes.object,
  minAnswers: PropTypes.number,
  isOpenEndedAttribute: PropTypes.bool.isRequired,
};

export default memo(QuestionnaireQuestionEntry);
